import { FC, useEffect } from "react";
import { Swiper as SwiperReact, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { Box, IconButton, Typography } from "@mui/material";
import styles from "./WelcomeOverviewCarousel.module.scss";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import WelcomeOverviewSkeleton from "./WelcomeOverviewSkeleton";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import { useTranslation } from "react-i18next";
import { ArrowIcon } from "../../components/icon-components/ArrowIcon";

interface Message {
  messageContextId: number;
  title: string;
  content: string;
  imageUrl: string;
}

interface WelcomeOverviewCarouselProps {
  welcomeOverviewData: Message[];
  error: boolean;
  isLoading: boolean;
  swiperRef: React.MutableRefObject<SwiperType | null>;
  onSlideChange: (index: number) => void;
  onNextClick: () => void;
  forceSlideToIndex: number | null;
  showBackArrow: boolean;
  handleBack: () => void;
}

const WelcomeOverviewCarousel: FC<WelcomeOverviewCarouselProps> = ({
  welcomeOverviewData,
  error,
  isLoading,
  swiperRef,
  onSlideChange,
  onNextClick,
  forceSlideToIndex,
  showBackArrow,
  handleBack,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (
      forceSlideToIndex !== null &&
      swiperRef.current &&
      swiperRef.current.activeIndex !== forceSlideToIndex
    ) {
      swiperRef.current.slideTo(forceSlideToIndex);
      swiperRef.current.update();
    }
  }, [forceSlideToIndex, swiperRef]);

  return (
    <Box>
      <DataLoader<Message[]>
        isLoading={isLoading}
        error={error}
        data={welcomeOverviewData}
        loadingComponent={<WelcomeOverviewSkeleton />}
        emptyComponent={<Typography>No welcome messages found.</Typography>}
        errorComponent={
          <Typography color="error">
            Error loading welcome overview data
          </Typography>
        }
      >
        {(messages) => (
          <Box className={styles.swiperContainer}>
            <SwiperReact
              modules={[Pagination]}
              pagination={{
                clickable: true,
                el: `.${styles.customSwiperPagination}`,
              }}
              spaceBetween={20}
              slidesPerView={1}
              loop={false}
              onSwiper={(swiper: SwiperType) => {
                swiperRef.current = swiper;
              }}
              onSlideChange={(swiper: SwiperType) => {
                onSlideChange(swiper.activeIndex);
              }}
            >
              {messages.map((message) => (
                <SwiperSlide key={message.messageContextId}>
                  <SlideView item={message} />
                </SwiperSlide>
              ))}
            </SwiperReact>

            <Box
              className={styles.controlsWrapper}
              sx={{
                gap: {
                  xs: "0rem",
                  sm: "4rem",
                  md: "4rem",
                },
              }}
            >
              <IconButton
                onClick={handleBack}
                sx={{
                  zIndex: 10,
                  boxShadow: "none",
                  visibility: showBackArrow ? "visible" : "hidden",
                }}
              >
                <ArrowIcon
                  width={24}
                  height={24}
                  fill="#1A1A1A"
                />
              </IconButton>

              <Box className={styles.customSwiperPagination} />
              <CustomButton
                sx={{ fontSize: "1rem" }}
                variant="contained"
                onClick={onNextClick}
                text={t("nextBtn")}
              />
            </Box>
          </Box>
        )}
      </DataLoader>
    </Box>
  );
};

interface SlideViewProps {
  item: Message;
}

const SlideView: FC<SlideViewProps> = ({ item }) => {
  return (
    <Box className={styles.slideView}>
      <Box className={styles.imageBox}>
        <img
          src={item.imageUrl}
          alt={item.title}
        />
      </Box>

      <Typography
        className={styles.title}
        variant="h1"
        sx={{
          marginTop: {
            xs: "4.06rem",
            sm: "4.06rem",
            md: "0rem",
          },
        }}
      >
        {item.title}
      </Typography>

      <Typography
        variant="body1"
        className={styles.content}
        dangerouslySetInnerHTML={{ __html: item.content }}
      />
    </Box>
  );
};

export default WelcomeOverviewCarousel;
